import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { ScrollToTopOnMount } from "frame/components";
import { canPinJobs } from "utils";
import CustomerForm from "./customer-add-edit-container";
import CustomerPinnedItems from "./customer-pinned-items";
import CustomerInfoCard from "./customer-info-card";
import CustomerSitesPanel from "./customer-sites-panel";
import CustomerPhotosNotesPanel from "./customer-photos-notes-panel";

const useStyles = makeStyles((theme) => ({
	sectionSlot: {
		margin: theme.spacing(2, 2, 0, 2),
		[theme.breakpoints.down("xs")]: {
			margin: theme.spacing(1, 1.5, 0, 1.5),
		},
	},
}));

const CUSTOMER_SECTION_INFO = "info";
const CUSTOMER_SECTION_SITES = "sites";
const CUSTOMER_SECTION_PHOTOS_NOTES = "photosNotes";

const Summary = ({
	customer,
	organisationId,
	role,
	onEdit,
	onDelete,
	onAddSite,
	onAddPhotoNote,
	siteList,
	onEditSite,
	onDeleteSite,
	onSelectSite,
}) => {
	const classes = useStyles();
	const allowUnpin = canPinJobs(role);
	const [expandedSection, setExpandedSection] = useState(CUSTOMER_SECTION_INFO);

	const onInfoExpanded = useCallback((event, isExpanded) => {
		setExpandedSection(isExpanded ? CUSTOMER_SECTION_INFO : null);
	}, []);

	const onSitesExpanded = useCallback((event, isExpanded) => {
		setExpandedSection(isExpanded ? CUSTOMER_SECTION_SITES : null);
	}, []);

	const onPhotosNotesExpanded = useCallback((event, isExpanded) => {
		setExpandedSection(isExpanded ? CUSTOMER_SECTION_PHOTOS_NOTES : null);
	}, []);

	if (!customer) {
		return null;
	}

	const knownPinnedCount =
		typeof customer.pinnedJobCount === "number"
			? customer.pinnedJobCount
			: undefined;

	return (
		<>
			<ScrollToTopOnMount />
			<CustomerForm />
			<CustomerPinnedItems
				customerId={customer.id}
				organisationId={organisationId}
				knownPinnedCount={knownPinnedCount}
				allowUnpin={allowUnpin}
			/>
			<div className={classes.sectionSlot}>
				<CustomerInfoCard
					customer={customer}
					role={role}
					onEdit={onEdit}
					onDelete={onDelete}
					expanded={expandedSection === CUSTOMER_SECTION_INFO}
					onExpandedChange={onInfoExpanded}
				/>
			</div>
			<div className={classes.sectionSlot}>
				<CustomerSitesPanel
					customer={customer}
					expanded={expandedSection === CUSTOMER_SECTION_SITES}
					onExpandedChange={onSitesExpanded}
					onAddSite={onAddSite}
					siteList={siteList}
					role={role}
					onEdit={onEditSite}
					onDelete={onDeleteSite}
					onSelectSite={onSelectSite}
				/>
			</div>
			<div className={classes.sectionSlot}>
				<CustomerPhotosNotesPanel
					customer={customer}
					organisationId={organisationId}
					expanded={expandedSection === CUSTOMER_SECTION_PHOTOS_NOTES}
					onExpandedChange={onPhotosNotesExpanded}
					onAddPhotoNote={onAddPhotoNote}
				/>
			</div>
		</>
	);
};

Summary.propTypes = {
	customer: PropTypes.object,
	organisationId: PropTypes.string,
	role: PropTypes.string,
	onEdit: PropTypes.func,
	onDelete: PropTypes.func,
	onAddSite: PropTypes.func,
	onAddPhotoNote: PropTypes.func,
	siteList: PropTypes.array,
	onEditSite: PropTypes.func,
	onDeleteSite: PropTypes.func,
	onSelectSite: PropTypes.func,
};

export default Summary;
